import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminDocSections, useUpsertDocSection, useDeleteDocSection, type DbDocSection } from "@/hooks/useDocSections";
import { docSections as staticSections } from "@/content/docs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Eye, EyeOff, FileText, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

interface EditorState {
  id?: string;
  slug: string;
  title: string;
  description: string;
  icon: string;
  content: string;
  sort_order: number;
  published: boolean;
}

const emptySection: EditorState = {
  slug: "",
  title: "",
  description: "",
  icon: "FileText",
  content: "",
  sort_order: 0,
  published: true,
};

export default function AdminDocs() {
  const { data: dbSections = [], isLoading } = useAdminDocSections();
  const upsert = useUpsertDocSection();
  const deleteMut = useDeleteDocSection();
  const [editing, setEditing] = useState<EditorState | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const dbSlugs = new Set(dbSections.map((s) => s.slug));
  const staticOnly = staticSections.filter((s) => !dbSlugs.has(s.slug));

  function openEditor(section?: DbDocSection) {
    if (section) {
      setEditing({
        id: section.id,
        slug: section.slug,
        title: section.title,
        description: section.description,
        icon: section.icon,
        content: section.content,
        sort_order: section.sort_order,
        published: section.published,
      });
    } else {
      setEditing({ ...emptySection, sort_order: dbSections.length });
    }
  }

  function importStatic(slug: string) {
    const s = staticSections.find((x) => x.slug === slug);
    if (!s) return;
    setEditing({
      slug: s.slug,
      title: s.title,
      description: s.description,
      icon: "FileText",
      content: s.content,
      sort_order: dbSections.length,
      published: true,
    });
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.slug || !editing.title) {
      toast.error("Slug and title are required");
      return;
    }
    try {
      await upsert.mutateAsync(editing as any);
      toast.success(editing.id ? "Section updated" : "Section created");
      setEditing(null);
    } catch (e: any) {
      toast.error(e.message || "Failed to save");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this section? This cannot be undone.")) return;
    try {
      await deleteMut.mutateAsync(id);
      toast.success("Section deleted");
    } catch (e: any) {
      toast.error(e.message || "Failed to delete");
    }
  }

  return (
    <div className="container px-6 py-8 max-w-4xl relative">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Documentation Sections</h2>
        <Button size="sm" onClick={() => openEditor()}>
          <Plus className="mr-2 h-4 w-4" />
          New Section
        </Button>
      </div>

      {/* DB sections */}
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
        Database Sections ({dbSections.length})
      </h3>
      <div className="space-y-3 mb-10">
        {dbSections.map((s) => (
          <Card key={s.id} className="flex items-center gap-4 p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <ArrowUpDown className="h-4 w-4" />
              <span className="text-xs w-6 text-center">{s.sort_order}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{s.title}</span>
                <Badge variant="outline" className="text-xs">/{s.slug}</Badge>
                {!s.published && (
                  <Badge variant="secondary" className="text-xs">
                    <EyeOff className="h-3 w-3 mr-1" />
                    Draft
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">{s.description}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" asChild>
                <Link to={`/docs/${s.slug}`}>
                  <Eye className="h-4 w-4" />
                </Link>
              </Button>
              <Button variant="ghost" size="icon" onClick={() => openEditor(s)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} className="text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
        {dbSections.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">No sections in database yet. Import from static or create new.</p>
        )}
      </div>

      {/* Static-only sections */}
      {staticOnly.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
            Static Fallback Sections ({staticOnly.length})
          </h3>
          <div className="space-y-3">
            {staticOnly.map((s) => (
              <Card key={s.slug} className="flex items-center gap-4 p-4">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{s.title}</span>
                    <Badge variant="outline" className="text-xs">/{s.slug}</Badge>
                    <Badge variant="secondary" className="text-xs">Static</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{s.description}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => importStatic(s.slug)}>
                  Import to DB
                </Button>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Editor Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Section" : "New Section"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Getting Started" />
                </div>
                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="getting-started" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Short description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icon (Lucide name)</Label>
                  <Input value={editing.icon} onChange={(e) => setEditing({ ...editing, icon: e.target.value })} placeholder="FileText" />
                </div>
                <div className="space-y-2">
                  <Label>Sort Order</Label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.published} onCheckedChange={(checked) => setEditing({ ...editing, published: checked })} />
                <Label>Published</Label>
              </div>
              <div className="space-y-2">
                <Label>Content (Markdoc)</Label>
                <Textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} placeholder="Write documentation in Markdown / Markdoc..." className="min-h-[300px] font-mono text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={handleSave} disabled={upsert.isPending}>{upsert.isPending ? "Saving..." : "Save"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
